import { View } from "ui/atoms/view";
import { Button, ButtonText } from "ui/gluestack/button";
import { Text } from "react-native";
import { FLUID_INTAKE__MAP } from "app/constants/ReportTypeConstant";
import { WaterIntakeButton } from "app/features/reporting/molecules/WaterIntakeButton";
import { useHandleFluidIntake } from "app/features/reporting/hooks/useHandleFluidIntake";
import { Controller } from "react-hook-form";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { Accordion } from "ui/gluestack/accordion";
import { FluidIntakeHistoryItem } from "../organisms/FluidIntakeHistoryItem";
import { useRef } from "react";
import { CreateFluidIntakeModal } from "../modals/CreateFluidIntakeModal";
import Pagination from "ui/molecules/pagination/Pagination";
import { formatDate } from "ui/utils/dateUtils";

export function FluidIntakeTemplate() {
  const {
    control,
    submit,
    isDirty,
    isSubmitting,
    history,
    handlePageChange,
    isHistoryLoading,
    fluidIntakeTotal,
    meta,
  } = useHandleFluidIntake({
    onSuccess: () => {},
  });
  const ref = useRef<React.ComponentRef<typeof CreateFluidIntakeModal>>(null);
  const handleShowModal = () => {
    ref.current?.show();
  };

  const isSubmitDisabled = isSubmitting || !isDirty;

  const todayDate = new Date();

  const totalTextColour =
    fluidIntakeTotal > 0 ? "text-typography-900" : "text-typography-300";

  return (
    <View className="resident-container">
      <CreateFluidIntakeModal ref={ref} />
      <View className="flex-row justify-between items-center">
        <Text className="text-lg text-primary-700 font-semibold">
          Fluid Intake
        </Text>
        <Button action="primary" size="sm" onPress={handleShowModal}>
          <ButtonText className="text-xs sm:text-base">
            Custom fluid intake log
          </ButtonText>
        </Button>
      </View>
      <View className="border border-gray-200 w-full mt-4 mb-11" />
      <View className="mb-11">
        <Text className="text-primary-400 text-base font-semibold mb-2">
          Total Fluid Intake
        </Text>
        <Text className="text-typography-600">{formatDate(todayDate)}</Text>
      </View>
      <View className="flex-row items-center gap-1.5">
        <Text className={`${totalTextColour} font-semibold text-4xl`}>
          {fluidIntakeTotal}
        </Text>
        <Text className="text-typography-600">ml</Text>
      </View>
      <View className="border border-gray-200 w-full mt-11 mb-11" />
      <View className="mb-11">
        <Text className="text-primary-400 text-base font-semibold mb-2">
          Quick Water Intake Log
        </Text>
        <Text className="text-typography-600">
          Tap on any of the options below and immediately log the water intake
          option for the resident.
        </Text>
        <View className="flex-row flex-wrap gap-6 self-start mt-11 mb-5">
          <Controller
            control={control}
            name="consumed_amount"
            render={({ field: { value, onChange } }) => (
              <>
                {FLUID_INTAKE__MAP.map(
                  ({ icon, label, value: intakeValue }, index) => (
                    <WaterIntakeButton
                      key={index}
                      icon={icon}
                      label={label}
                      selected={value === intakeValue}
                      onPress={() => {
                        onChange(intakeValue);
                      }}
                    />
                  ),
                )}
              </>
            )}
          />
        </View>

        <Button
          onPress={submit}
          className="w-fit self-end"
          disabled={isSubmitDisabled}
        >
          <ButtonText>Submit log</ButtonText>
        </Button>

        <View className="border border-gray-200 w-full mt-11" />
      </View>
      <View className="mb-11">
        <Text className="text-primary-400 text-base font-semibold mb-2">
          Intake Log
        </Text>
        {isHistoryLoading ? (
          <LoadingSpinner />
        ) : (
          <View className="flex-column gap-3 ">
            <Accordion type="single" variant="filled" className="w-full">
              {history?.map((log) => (
                <FluidIntakeHistoryItem key={log.id} log={log} />
              ))}
            </Accordion>
          </View>
        )}
        <Pagination meta={meta} onPageChange={handlePageChange} />
      </View>
    </View>
  );
}
